import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch, withDefaults } from 'vue';
import type { Contributor, ContributorNameComponents } from '@ilteducation/content-ref-data-types';
import {
  contributorFullName,
  creatorNameComponents,
  fullNameFromComponents,
  nameComponentsOf,
} from '@/foundations/helpers/contributorName';
import { useContributorSearch } from '@/comp/use-contributor-search';
import router from '@/router';
import type { BookCreatorDraft } from '@/types/bookCreator';

type Props = {
  value: BookCreatorDraft;
  /** Set when the `extra` slot is filled, so the name fields make room for it. */
  hasExtraField?: boolean;
};
type Emits = {
  (e: 'input', draft: Partial<BookCreatorDraft>): void;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'CreatorNameFields',
  props: {
    value: null,
    hasExtraField: { type: Boolean, default: false }
  },
  emits: ["input"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'input', draft: Partial<BookCreatorDraft>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: BookCreatorDraft, hasExtraField: boolean }





/**
 * Own copy of the name being edited: the rows ArrayInput builds only carry the
 * keys the book payload had, and Vue 2 cannot track properties added afterwards.
 * Changes are emitted for the parent to merge back into the row. The link to a
 * contributor is not copied here - it is read off the row, which the save also
 * writes to when it creates the contributor.
 */
const EMPTY_NAME = { title: '', firstName: '', middleName: '', lastName: '', suffix: '' };

const draft = ref({ ...EMPTY_NAME });

const seedFrom = (creator: BookCreatorDraft) => {
  draft.value = { ...EMPTY_NAME, ...creatorNameComponents(creator) };
};

seedFrom(props.value);

// Fetching book metadata replaces the creator rows wholesale, and ArrayInput can
// hand a fresh row to this same instance - so follow the row it is given.
watch(() => props.value, seedFrom);

// Immediate, so the row starts out holding the components resolved above: a row
// arrives with `nameComponents`, and from then on the flat components are the
// form's copy - and the only one the payload is rebuilt from.
watch(draft, () => emits('input', { ...draft.value, nameComponents: undefined }), { deep: true, immediate: true });

const notEmpty = (val: string) => (val || '').length > 0 || 'This field is required';
const trimmedString = (val: string) =>
  !/^\s+|\s+$|\s{2,}/.test(val || '') || 'Leading or trailing whitespaces are not allowed';

const fullName = computed(() => fullNameFromComponents(draft.value));
const isLinked = computed(() => !!props.value.contributorId);

// A linked row carries its contributor's name, so it is not validated - an
// organization, for one, has no first name to give. Editing the name unlinks it.
const nameRules = computed(() => (isLinked.value ? [] : [trimmedString]));
const requiredNameRules = computed(() => (isLinked.value ? [] : [notEmpty, trimmedString]));

// The narrator language field shares the row with the name, so the name fields
// give up a column each to make room for it. Without it they fill the row.
const nameCols = props.hasExtraField
  ? { title: 1, firstName: 3, middleName: 2, lastName: 3, suffix: 1 }
  : { title: 2, firstName: 3, middleName: 3, lastName: 3, suffix: 1 };

const contributorHref = computed(() =>
  props.value.contributorId
    ? router.resolve({ name: 'contributors_edit', params: { id: props.value.contributorId } }).href
    : undefined,
);

const { results, isSearching } = useContributorSearch(
  fullName,
  computed(() => !isLinked.value),
);

const link = (contributor: Contributor, name: ContributorNameComponents = contributor.name) => {
  draft.value = { ...EMPTY_NAME, ...nameComponentsOf(name) };
  emits('input', { contributorId: contributor.id, alternativeNames: contributor.alternativeNames });
};

const unlink = () => {
  if (isLinked.value) {
    emits('input', { contributorId: undefined, alternativeNames: undefined });
  }
};

// The top result is linked without a click when the entered name is exactly one
// of its names. Linking by an alternative name keeps that name on the row.
watch(results, ([topResult]) => {
  const enteredName = fullName.value.toLowerCase();
  const matchingName = [topResult?.name, ...(topResult?.alternativeNames ?? [])].find(
    (name) => name && fullNameFromComponents(name).toLowerCase() === enteredName,
  );
  if (!isLinked.value && matchingName) {
    link(topResult, matchingName);
  }
});

return { __sfc: true,props, emits, EMPTY_NAME, draft, seedFrom, notEmpty, trimmedString, fullName, isLinked, nameRules, requiredNameRules, nameCols, contributorHref, results, isSearching, link, unlink, contributorFullName }
}

})